/**
 * Tab Nóminas - Pago de nóminas con la remesa del BBVA.
 * Lista las remesas de cada mes (total y estado) con la descarga del Excel del
 * banco; "Ver detalle" abre la misma ventana de la remesa que Empleados.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Banknote, Download, Eye, RefreshCw, AlertTriangle, CheckCircle } from 'lucide-react';
import { nominasAPI } from '../../api/api';
import RemesaNominasModal from '../RemesaNominasModal';

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const formatoImporte = (valor) => {
  if (valor === null || valor === undefined) return 'Sin importe';
  return `${Number(valor).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
};

// Las fechas del backend son UTC sin zona (datetime.utcnow().isoformat())
const formatoDia = (iso) => {
  if (!iso) return null;
  const texto = /Z|[+-]\d{2}:\d{2}$/.test(iso) ? iso : `${iso}Z`;
  const d = new Date(texto);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const clavePeriodo = (anio, mes) => `${anio}-${String(mes).padStart(2, '0')}`;

const Estado = ({ remesa }) => {
  if (remesa.pagada_en) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
        <CheckCircle className="w-3 h-3" />
        Pagada el {formatoDia(remesa.pagada_en)}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
      Pendiente de pagar
    </span>
  );
};

const NominasTab = () => {
  const [remesas, setRemesas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [detalle, setDetalle] = useState(null); // 'AAAA-MM' abierto en la ventana

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const { data } = await nominasAPI.listar();
      setRemesas(data?.remesas || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudieron cargar las remesas de nóminas');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const descargar = (r) => {
    window.open(nominasAPI.urlExcel(r.anio, r.mes), '_blank');
  };

  const hoy = new Date();
  const mesActual = clavePeriodo(hoy.getFullYear(), hoy.getMonth() + 1);

  return (
    <div className="space-y-4 mt-6" data-testid="nominas-panel">
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-emerald-50 text-duetto-primary flex items-center justify-center shrink-0">
            <Banknote className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-slate-900">Pago de nóminas</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Remesa para el BBVA con el líquido de cada nómina de a3doc. Descarga el Excel y súbelo a la banca para pagar.
            </p>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            type="button"
            onClick={cargar}
            disabled={cargando}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${cargando ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
          <button
            type="button"
            onClick={() => setDetalle(mesActual)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-duetto-primary text-white hover:opacity-90"
            data-testid="nominas-abrir-mes"
          >
            <Eye className="w-3.5 h-3.5" />
            Remesa de {MESES[hoy.getMonth()].toLowerCase()}
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-guia="finanzas-nominas">
        {!cargando && !error && remesas.length === 0 && (
          <p className="text-sm text-slate-500 px-4 py-6 text-center">
            Todavía no hay remesas. Se preparan solas cuando la gestoría sube las nóminas a a3doc.
          </p>
        )}
        {remesas.length > 0 && (
          <ul className="divide-y divide-slate-100">
            {remesas.map((r) => {
              const clave = clavePeriodo(r.anio, r.mes);
              return (
                <li key={clave} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`nominas-remesa-${clave}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-slate-900">{MESES[r.mes - 1]} {r.anio}</span>
                      <Estado remesa={r} />
                      {!r.completa && !r.pagada_en && (
                        <span className="text-xs text-amber-700">Con avisos: revisa el detalle</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Total {formatoImporte(r.total)}
                      {formatoDia(r.descargado_en) ? ` · Descargada el ${formatoDia(r.descargado_en)}` : ' · Sin descargar'}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => setDetalle(clave)}
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      Ver detalle
                    </button>
                    <button
                      type="button"
                      onClick={() => descargar(r)}
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-duetto-primary text-white hover:opacity-90"
                      data-testid={`nominas-descargar-${clave}`}
                    >
                      <Download className="w-3.5 h-3.5" />
                      Descargar Excel BBVA
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <RemesaNominasModal
        open={!!detalle}
        onClose={() => { setDetalle(null); cargar(); }}
        mesInicialStr={detalle}
      />
    </div>
  );
};

export default NominasTab;
